import React, { useState, useContext } from "react";
import { Box, Typography, CircularProgress, List, ListItem, TextField, IconButton, Paper, Popover, MenuList, MenuItem } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import { ThemeContext } from "../../../context/ThemeContext";
import CloseIcon from "@mui/icons-material/Close";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SearchIcon from "@mui/icons-material/Search";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PullToRefresh from 'react-pull-to-refresh';
import { useSelector,useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getNews } from "../../../api/request/dashboard/dashboard";
import { setNews } from "../../../store/Slices/newsSlice";

export default function News() {
  const { darkMode } = useContext(ThemeContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [newsProvider, setNewsProvider] = useState("");
  const { news = [] } = useSelector((state) => state.news);
  const newsProviderName = [...new Set(news?.map(news => news.newsProviderName))].filter(Boolean);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true); // Start refresh loading
    try {
      const response = await getNews();
      dispatch(setNews(response.data)); // Update Redux store with new news
    } catch (error) {
      console.error("Error fetching news:", error);
    }
    setIsRefreshing(false); // Stop refresh loading
  };

  if (isRefreshing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="20vh"><CircularProgress /></Box>
    );
  }

  if (!news || news.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6">{t('nonewsavailable')}</Typography>
      </Box>
    );
  }

  const handleReadMore = (link) => {
    window.open(link, "_blank");
  };

  const handleToggleSearchBar = () => {
    setShowSearchBar(prev => !prev);
    setSearchQuery('');
  };

  const handleOpenFilterMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectSymbol = (symbol) => {
    setNewsProvider(symbol === "All" ? "" : symbol);
    setAnchorEl(null);
  };

  const filteredNews = news.filter(news =>
    (newsProvider ? news.newsProviderName === newsProvider : true) &&
    (news.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      news.newsProviderName.toLowerCase().includes(searchQuery.toLowerCase()))
  );


  const actions = [
    { icon: <SearchIcon />, name: `${t('search')}`, onClick: handleToggleSearchBar },
    { icon: <CurrencyExchangeIcon />, name: `${t('filterbySymbol')}`, onClick: handleOpenFilterMenu }
  ];
  return (
    <Box p={0} sx={{ position: 'relative' }}>
      {showSearchBar && (
        <Paper sx={{ position: "absolute", top: 10, right: 10, padding: "1px", zIndex: 10, display: "flex", alignItems: "center", borderRadius: 50, width: '25%', boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)", backgroundColor: darkMode ? "#202933" : "#d5d5d5" }}>
          <TextField size="small" variant="outlined" placeholder={t("search")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ "& fieldset": { border: "none" }, flex: 1, }} />
          <IconButton onClick={handleToggleSearchBar}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
        </Paper>
      )}
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleCloseFilterMenu} anchorOrigin={{ vertical: "top", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuList sx={{ backgroundColor: darkMode ? "#202933" : "#d5d5d5", }}>
          {newsProviderName.length > 0 && (
            <MenuItem onClick={() => handleSelectSymbol("All")} sx={{ fontSize: 13.5, }}>{t('allNewsProvider')}</MenuItem>
          )}
          {newsProviderName.length > 0 ? newsProviderName.map((symbol, index) => (
            <MenuItem key={index} onClick={() => handleSelectSymbol(symbol)} sx={{ fontSize: 13.5, }}>{symbol}</MenuItem>
          )) : <MenuItem disabled sx={{ fontSize: 13.5, }}>{t('noNewsProvider')}</MenuItem>}
        </MenuList>
      </Popover>

      <SpeedDial ariaLabel="SpeedDial search filter" sx={{ position: "fixed", bottom: 0, right: 10, transform: "scale(0.7)" }} icon={<SpeedDialIcon />}>
        {actions.map((action) => (
          <SpeedDialAction key={action.name} icon={action.icon} tooltipTitle={action.name} onClick={action.onClick} />
        ))}  </SpeedDial>

      <Box sx={{ paddingTop: 1, }}>
        <PullToRefresh onRefresh={handleRefresh}>
          <List>
            {filteredNews.map((news) => (
              <ListItem key={news.id} sx={{ marginBottom: 1, borderBottom: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, paddingBottom: 1 }}>
                <Box onClick={() => handleReadMore(news.link)}>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center", marginBottom: 1, color: darkMode ? "var(--lightText)" : 'var(--darkText)', }}>{news.title}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }} >
                    <AccessTimeIcon sx={{ marginRight: 1, fontSize: "13px", color: darkMode ? "var(--lightText)" : 'var(--darkText)', }} />
                    <Typography color="textSecondary" sx={{ marginRight: 2, fontSize: "12px" }} > {new Date(news.dateTime).toLocaleString()}</Typography>
                    <PersonIcon sx={{ marginRight: 1, fontSize: "14px", color: darkMode ? "var(--lightText)" : 'var(--darkText)', }} />
                    <Typography color="textSecondary" sx={{ marginRight: 2, fontSize: "12px" }}> {news.newsProviderName}</Typography>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </PullToRefresh>
      </Box>
    </Box>
  );
}

